import { useEffect, useState } from "react";
import { Outlet, useParams, useLocation } from "react-router-dom";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import ObjectHeading from "@/components/ObjectHeading";
import { Params, Path, useNavigate } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import Tabs from "@/components/Tabs";
import Button from "@/components/Button";
import { useQuery } from "@apollo/client";
import { GET_ACTIVITY } from "../../../queries";
import { renderCredits } from "../../../_utils";
import { toUTCFormat } from "@/lib/dateHelpers";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import { useRecentlyVisited } from "@/hooks/useRecentlyVisited";
import { toActivityRecentlyVisitedItem } from "./activityRecentlyVisited";
import { useSearchParams } from "react-router-dom";
import { ObjectDetailPageSkeleton } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";

export interface ActivityOutletContext {
  activityData: any;
}
export default function ActivityInstanceLayout() {
  const [searchParams] = useSearchParams();
  const { activityTypeId = "", activityId = "" } = useParams();
  const { data, loading, error, refetch } = useQuery<any>(GET_ACTIVITY, {
    variables: {
      id: activityId
    },
    notifyOnNetworkStatusChange: true
  });
  const returnUrl = searchParams.get("returnUrl");
  const backToLinkOrDefault = returnUrl
    ? decodeURIComponent(returnUrl)
    : `/activities/activity-types/${activityTypeId}`;
  const [backLink] = useState<LinkProps<Path, Params>>({
    to: backToLinkOrDefault as any,
    title: "Back"
  } as unknown as LinkProps<Path, Params>);

  const navigate = useNavigate();
  const location = useLocation();

  const canAccessActivityManagement = useActivityTypePermissions(
    data?.activity?.activityType || {},
    data?.activity?.managingSection?.id
  );
  const { addRecentlyVisitedItem } = useRecentlyVisited({ skipListQuery: true });
  const isEditPage = location.pathname.includes("/edit");
  const isAddParticipantsPage = location.pathname.includes("/add-participants");
  const activityData = data?.activity;
  const creditRates = activityData?.activityType?.config?.creditRates ?? [];
  const loadedActivityId = typeof activityData?.id === "string" ? activityData.id : "";
  const activityTitle = typeof activityData?.title === "string" ? activityData.title : "";
  const activityCreatedAt = typeof activityData?.createdAt === "string" ? activityData.createdAt : "";
  const allowParticipationStarting =
    typeof activityData?.allowParticipationStarting === "string" ? activityData.allowParticipationStarting : null;
  const allowParticipationThrough =
    typeof activityData?.allowParticipationThrough === "string" ? activityData.allowParticipationThrough : null;
  const eventSlug = typeof activityData?.event?.slug === "string" ? activityData.event.slug : null;
  const eventStartsAt = typeof activityData?.event?.startsAt === "string" ? activityData.event.startsAt : null;
  const eventEndsAt = typeof activityData?.event?.endsAt === "string" ? activityData.event.endsAt : null;
  const hasEvent = Boolean(activityData?.event);

  useEffect(() => {
    if (isEditPage || isAddParticipantsPage) {
      return;
    }
    const item = toActivityRecentlyVisitedItem({
      activityTypeId,
      activityId,
      url: location.pathname,
      activity: {
        id: loadedActivityId,
        title: activityTitle,
        createdAt: activityCreatedAt,
        allowParticipationStarting,
        allowParticipationThrough,
        event: hasEvent
          ? {
              slug: eventSlug,
              startsAt: eventStartsAt,
              endsAt: eventEndsAt
            }
          : null
      }
    });
    if (!item) {
      return;
    }
    addRecentlyVisitedItem(item);
  }, [
    activityId,
    activityTypeId,
    loadedActivityId,
    activityCreatedAt,
    activityTitle,
    allowParticipationStarting,
    allowParticipationThrough,
    eventSlug,
    eventStartsAt,
    eventEndsAt,
    hasEvent,
    location.pathname,
    isEditPage,
    isAddParticipantsPage,
    addRecentlyVisitedItem
  ]);

  if (isEditPage || isAddParticipantsPage) {
    return <Outlet />;
  }

  const contextValue = {
    activityData
  };

  if (error && !loading && !activityData?.createdAt) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <EmptyState
          iconName="error"
          title="Activity Didn't Load"
          caption="Try loading this activity again."
          action={{
            children: "Try again",
            onClick: () => {
              void refetch();
            }
          }}
        />
      </FocusedPageLayout>
    );
  }

  // cache-and-network can return a list-shaped Activity (no createdAt) before GET_ACTIVITY lands.
  if (loading && (!activityData || !activityData.createdAt)) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <ObjectDetailPageSkeleton />
      </FocusedPageLayout>
    );
  }

  if (!activityData) {
    return (
      <FocusedPageLayout backLink={backLink}>
        <div className="text-title-large">Activity not found</div>
      </FocusedPageLayout>
    );
  }

  // Determine which tab should be active based on the current path
  const isParticipantsTab = location.pathname.includes("/participants");

  const tabItems = [
    {
      label: "Overview",
      link: `/activities/activity-types/${activityTypeId}/activity/${activityId}` as Path,
      active: !isParticipantsTab
    },
    {
      label: "Participants",
      link: `/activities/activity-types/${activityTypeId}/activity/${activityId}/participants` as Path,
      active: isParticipantsTab
    }
  ];

  return (
    <FocusedPageLayout
      backLink={backLink}
      actions={
        <>
          {activityData.event?.slug && (
            <Button
              variant="outlined"
              icon="open_in_new"
              onClick={() => navigate(`/activities/events/${activityData.event.slug}/overview` as Path)}
            >
              View Associated Event
            </Button>
          )}
          <Button
            variant="filled"
            disabled={!canAccessActivityManagement}
            onClick={() =>
              navigate(`/activities/activity-types/${activityTypeId}/activity/${activityId}/edit` as Path, {
                state: { activity: activityData }
              })
            }
          >
            Edit Activity
          </Button>
        </>
      }
      stickyActions={true}
    >
      <ObjectHeading
        title={activityData.title}
        metaData={[
          {
            text: `${renderCredits(creditRates, activityData.duration)}`,
            label: "PDR Credits"
          },
          {
            text: `${activityData.activityType?.title ?? ""}`,
            label: "Activity Type"
          },
          ...(activityData.allowParticipationStarting
            ? [
                {
                  text: toUTCFormat(activityData.allowParticipationStarting) || "",
                  label: "Starts On"
                }
              ]
            : []),
          {
            text: activityData.allowParticipationThrough
              ? toUTCFormat(activityData.allowParticipationThrough) || ""
              : "(None)",
            label: "Ends On"
          }
        ]}
        iconName="description"
        isDetailsView
      />
      <Tabs tabItems={tabItems} />
      <div data-testid="activity-instance-page">
        <Outlet context={contextValue} />
      </div>
    </FocusedPageLayout>
  );
}
